import { Divider, Modal, ModalClose, ModalDialog, Stack, styled, Typography } from '@healthinal/ui';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  PatientAddressDtoReferenceType,
} from '../../../../../api/generated/model';
import {
  getGetIsolationByPatientQueryKey,
  useCreateIsolationByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { queryClient } from '../../../../../api/queryClient.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { CreatePatientAddressForm } from '../../../../components/patient-address/CreatePatientAddressForm.tsx';

interface FormValues {
  from: string;
  until: string;
  address: AddressDto | null;
  contact: AddressContactDto | null;
}
export function CreateIsolationDialog({
  open,
  onClose,
  patientId,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
}) {
  const formMethods = useForm<FormValues>({
    defaultValues: {
      from: '',
      until: '',
      address: null,
      contact: null,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty: isFormDirty },
  } = formMethods;

  const createIsolationMutation = useCreateIsolationByPatient({
    mutation: {
      onSuccess: () => {
        toast.success('Isolation wurde erfolgreich gespeichert');
        void queryClient.invalidateQueries({ queryKey: getGetIsolationByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => {
        toast.error('Bei dem Speichern der Isolation ist ein Fehler aufgetreten');
      },
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 600 }}>
        <ModalClose />
        <Typography level={'h4'}>Isolation</Typography>
        <Divider />
        <FormProvider {...formMethods}>
          <form
            onSubmit={handleSubmit((data) =>
              createIsolationMutation.mutate({
                id: patientId,
                data: {
                  address: data.address!,
                  contact: data.contact ?? undefined,
                  from: data.from,
                  until: data.until,
                  patientId: patientId,
                  referenceType: PatientAddressDtoReferenceType.PATIENT_ISOLATION,
                },
              }),
            )}>
            <Stack gap={2}>
              <FormGrid>
                <CreatePatientAddressForm
                  from={'from'}
                  until={'until'}
                  address={'address'}
                  contact={'contact'}
                  fromInputName={'Eintritt'}
                  untilInputName={'Austritt'}
                  addressInputName={'Ort'}
                  contactInputName={'Kontakt'}
                  addressTypes={Object.values(AddressDtoAddressType)}
                />
              </FormGrid>
              <FormActionButtons
                onAbortClick={handleOnClose}
                disableSubmit={!isFormDirty}
                isLoading={createIsolationMutation.isPending}
              />
            </Stack>
          </form>
        </FormProvider>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
